import React, { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Col, Form, Row } from 'antd';
import { normalizeDecimal } from '@core/utils/methods';
import { ACTUAL_DATE } from '@core/utils/validators';
import { YES_NO_GROUP } from '@models/constants';
import { EditableInformationProps } from '@models/interfaces';
import { Panel } from '@shared/components';
import {
  Autocomplete,
  DatePicker,
  Input,
  InputNumber,
  RadioGroup,
} from '@shared/modules';
import { DDVSelectors, PaceTasksSelectors } from '@store/selectors';

export const PermittingProjectInformation: FC<EditableInformationProps> = ({
  isEditing = false,
  data,
  form,
}: EditableInformationProps) => {
  const dropdownsOptions = useSelector(DDVSelectors.getDDVOptions);
  const currentPaceTasks = useSelector(PaceTasksSelectors.getPaceTasks);

  const scopingCompleteFromStore = currentPaceTasks.find(
    task => task.taskName === 'Scoping Complete',
  )?.projectPaceTaskData?.actualDate;

  const finalRFDSReceivedFromStore = currentPaceTasks.find(
    task => task.taskName === 'Final RFDS Received',
  )?.projectPaceTaskData?.actualDate;

  useEffect(() => {
    if (data) {
      form?.setFieldsValue(data);
    }

    if (scopingCompleteFromStore) {
      form?.setFieldsValue({ scopingComplete: scopingCompleteFromStore });
    }

    if (finalRFDSReceivedFromStore) {
      form?.setFieldsValue({ finalRFDSReceived: finalRFDSReceivedFromStore });
    }
  }, [scopingCompleteFromStore]);

  return (
    <Panel header="Project Information">
      <Form wrapperCol={{ span: 14 }} labelCol={{ span: 12 }} form={form}>
        <Row>
          <Col span="12">
            <Input id="projectType" label="Project Type" isEditing={false} />
            <DatePicker
              id="scopingComplete"
              label="Scoping Complete"
              isEditing={isEditing}
              formItemProps={{
                rules: ACTUAL_DATE(data?.scopingComplete),
              }}
            />
            <DatePicker
              id="finalRFDSReceived"
              label="Final RFDS Date"
              isEditing={isEditing}
              formItemProps={{
                rules: ACTUAL_DATE(data?.finalRFDSReceived),
              }}
            />
            <Autocomplete
              id="previousMountModels"
              label="Mount Types"
              isEditing={isEditing}
              elementProps={{
                mode: 'multiple',
              }}
              options={dropdownsOptions?.MountModel}
            />
            <RadioGroup
              id="isCraneRequired"
              label="Crane Required"
              isEditing={isEditing}
              buttons={YES_NO_GROUP}
            />
            <InputNumber
              id="craneHeight"
              label="Crane Height"
              isEditing={isEditing}
            />
            <Input
              id="civilVendor"
              label="Civil Vendor"
              isEditing={isEditing}
            />
          </Col>
        </Row>
        <Row>
          <Col span="12">
            <InputNumber
              id="azimuthA"
              label="Azimuth A"
              isEditing={isEditing}
            />
            <InputNumber
              id="azimuthB"
              label="Azimuth B"
              isEditing={isEditing}
            />
            <InputNumber
              id="azimuthG"
              label="Azimuth G"
              isEditing={isEditing}
            />
            <InputNumber
              id="azimuthD"
              label="Azimuth D"
              isEditing={isEditing}
            />
          </Col>
          <Col span="12">
            <Input
              id="RADA"
              label="RAD A"
              isEditing={isEditing}
              formItemProps={{
                normalize: normalizeDecimal,
              }}
            />
            <Input
              id="RADB"
              label="RAD B"
              isEditing={isEditing}
              formItemProps={{
                normalize: normalizeDecimal,
              }}
            />
            <Input
              id="RADG"
              label="RAD G"
              isEditing={isEditing}
              formItemProps={{
                normalize: normalizeDecimal,
              }}
            />
            <Input
              id="RADD"
              label="RAD D"
              isEditing={isEditing}
              formItemProps={{
                normalize: normalizeDecimal,
              }}
            />
          </Col>
        </Row>
      </Form>
    </Panel>
  );
};
